import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { X, Maximize2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export function Gallery() {
  const [filter, setFilter] = useState('ALL');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      const { data } = await supabase
        .from('gallery_items')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (data) setImages(data);
      setLoading(false);
    };
    fetchGallery();
  }, []);

  const categories = ['ALL', ...Array.from(new Set(images.map(img => img.category)))];

  const filteredImages = filter === 'ALL' 
    ? images 
    : images.filter(img => img.category === filter);

  if (loading && images.length === 0) {
    return (
      <section className="py-24 bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176B5B]" />
      </section>
    );
  }

  return (
    <section id="gallery" className="py-24 lg:py-40 bg-background px-6">
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-20 gap-12">
          <div className="max-w-3xl">
            <span className="font-serif !text-[#B8863B] mb-6 block lowercase italic text-xl">
              Visual Journey
            </span>
            <h2 className="heading-luxury font-bold !text-[#176B5B]">
              ESTATE <br />
              <span className="italic !text-[#B8863B]">GALLERY.</span>
            </h2>
          </div>
          
          <div className="flex flex-wrap gap-4 lg:gap-8 border-b border-[#176B5B]/5 pb-4 max-w-2xl lg:justify-end">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={cn(
                  "transition-all duration-500 pb-2 relative font-clean font-bold uppercase tracking-widest text-xs",
                  filter === cat ? "!text-[#176B5B] opacity-100" : "!text-[#176B5B]/40 hover:text-[#176B5B]/70"
                )}
              >
                {cat.replace('_', ' ')}
                {filter === cat && (
                  <motion.div 
                    layoutId="activeFilter"
                    className="absolute bottom-0 left-0 right-0 h-[1px] !bg-[#B8863B]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        <motion.div 
          layout
          className="columns-2 lg:columns-3 gap-6 md:gap-8 space-y-6 md:space-y-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredImages.map((image) => (
              <motion.div
                layout
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative group cursor-pointer break-inside-avoid"
                onClick={() => setSelectedImage(image.url)}
              >
                <div className="relative overflow-hidden bg-secondary">
                  <img
                    src={image.url}
                    alt={image.alt || 'Walnut Farm'}
                    className="w-full h-auto transition-transform duration-[1.5s] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white scale-0 group-hover:scale-100 transition-transform duration-500 delay-100">
                      <Maximize2 className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="text-[10px] font-bold tracking-widest uppercase !text-[#3B7D78] font-clean">{image.category.replace('_', ' ')}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-primary/95 backdrop-blur-xl flex items-center justify-center p-6 lg:p-12"
            onClick={() => setSelectedImage(null)}
          >
            <button className="absolute top-8 right-8 text-white/60 hover:text-white transition-colors">
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage}
              className="max-w-full max-h-full object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
